import { yupResolver } from '@hookform/resolvers/yup';
import { Radio, Space } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import AnimateHeight from 'react-animate-height';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import selectors from '../../../store/selectors';
import { mobileOrHomePhoneRegexPattern, nameRegexPattern } from '../../../utils/string';
import FormInputError from '../../Form/FormInputError';
import OfferingAmount from '../../Form/OfferingAmount';
import SubmitAddMassOffering from '../../Form/rhf/SubmitAddMassOffering/SubmitAddMassOffering';

import { Address } from './Address';
import { perpetualMassFormNames } from './perpetualMassFormNames';

const PERPETUAL_OFFERING_TYPES = {
  living: 'for_living_person',
  deceased: 'for_deceased_person',
};

export const PerpetualMassesForm = ({ onSubmit, loading }) => {
  const perpetualMassesBeforeFormText = useSelector(selectors.selectPerpetualMassBeforeFormText);
  const perpetualMassesFormAddressField = useSelector(
    selectors.selectPerpetualMassOfferingFormAddressField,
  );
  const perpetualMassOfferingAmount = useSelector(
    selectors.selectPerpetualMassOfferingAmountOption,
  );

  const defaultValues = useMemo(() => {
    return {
      [perpetualMassFormNames.agreeOnRegulations]: false,
      [perpetualMassFormNames.email]: '',
      [perpetualMassFormNames.fullName]: '',
      [perpetualMassFormNames.offeringAmount]:
        perpetualMassOfferingAmount.predefinedValues.length > 1
          ? perpetualMassOfferingAmount.predefinedValues[1]
          : null,
      [perpetualMassFormNames.offeringContent]: '',
      [perpetualMassFormNames.offeringType]: PERPETUAL_OFFERING_TYPES.living,
      [perpetualMassFormNames.phone]: '',
      [perpetualMassFormNames.showAddress]: false,
      [perpetualMassFormNames.country]: 'Polska',
      [perpetualMassFormNames.city]: '',
      [perpetualMassFormNames.zipCode]: '',
      [perpetualMassFormNames.street]: '',
    };
  }, []);

  const regulationsUrl = useSelector(selectors.selectRegulationsUrl);

  const formSchema = useMemo(() => {
    return yup.object().shape({
      [perpetualMassFormNames.agreeOnRegulations]: yup
        .bool()
        .oneOf([true], 'Musisz zgodzić się na regulamin, żeby zamówić Mszę Świętą wieczystą'),
      [perpetualMassFormNames.email]: yup.string().email().required('To pole jest wymagane'),
      [perpetualMassFormNames.fullName]: yup
        .string()
        .matches(new RegExp(nameRegexPattern, 'u'), 'Podaj poprawne imię i nazwisko'),
      [perpetualMassFormNames.offeringAmount]: yup
        .number()
        .min(perpetualMassOfferingAmount.minimalValue, 'Podaj poprawną kwotę')
        .required('To pole jest wymagane')
        .typeError('Podaj poprawną kwotę (tylko cyfry)'),
      [perpetualMassFormNames.offeringContent]: yup
        .string()
        .matches(new RegExp(nameRegexPattern, 'u'), 'Podaj poprawne imię i nazwisko'),
      [perpetualMassFormNames.offeringType]: yup
        .string()
        .oneOf(Object.values(PERPETUAL_OFFERING_TYPES))
        .required('To pole jest wymagane'),
      [perpetualMassFormNames.phone]: yup.string().matches(
        // /(^((\+48)|(0048))?[ -]?[0-9]{3}[ -]?[0-9]{3}[ -]?[0-9]{3}$)|(^[0-9]{2}?[ -]?[0-9]{3}[ -]?[0-9]{2}[ -]?[0-9]{2}$)/
        new RegExp(mobileOrHomePhoneRegexPattern),
        {
          excludeEmptyString: true,
          message:
            'Podaj poprawny numer telefonu, np. w formacie 123 456 789 lub 22 123 12 12, lub +48 123 456 789',
        },
      ),
      [perpetualMassFormNames.showAddress]: yup.bool(),
      [perpetualMassFormNames.country]: yup.string().when(perpetualMassFormNames.showAddress, {
        is: true,
        then: yup.string().required('To pole jest wymagane'),
      }),
      [perpetualMassFormNames.city]: yup.string().when(perpetualMassFormNames.showAddress, {
        is: true,
        then: yup.string().required('To pole jest wymagane'),
      }),
      [perpetualMassFormNames.zipCode]: yup
        .string()
        .matches(new RegExp(`^[0-9]{2}-[0-9]{3}$`), {
          excludeEmptyString: true,
          message: 'Format: 00-000',
        })
        .when(perpetualMassFormNames.showAddress, {
          is: true,
          then: yup.string().required('To pole jest wymagane'),
        }),
      [perpetualMassFormNames.street]: yup.string().when(perpetualMassFormNames.showAddress, {
        is: true,
        then: yup.string().required('To pole jest wymagane'),
      }),
    });
  }, [perpetualMassOfferingAmount.minimalValue]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    setError,
    clearErrors,
    watch,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const showAddress = watch(perpetualMassFormNames.showAddress);

  return (
    <div>
      {/* eslint-disable-next-line react/no-danger */}
      <div dangerouslySetInnerHTML={{ __html: perpetualMassesBeforeFormText }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Space direction="vertical" className="d-flex mt-4" size="large">
          <div>
            <div className="md-form input-group mt-0 mb-0">
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                disabled={loading}
                className="form-control"
                type="text"
                id={perpetualMassFormNames.offeringContent}
                {...register(perpetualMassFormNames.offeringContent)}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor={perpetualMassFormNames.offeringContent}>
                Imię i nazwisko osoby wpisywanej do Księgi Mszy Świętych Wieczystych
              </label>
            </div>

            {errors[perpetualMassFormNames.offeringContent] && (
              <FormInputError errorText={errors[perpetualMassFormNames.offeringContent].message} />
            )}
          </div>

          <div>
            <Space direction="horizontal" size="large">
              Intencja za osobę:
              <Controller
                control={control}
                name={perpetualMassFormNames.offeringType}
                render={({ field }) => (
                  <Radio.Group
                    disabled={loading}
                    name={field.name}
                    onChange={field.onChange}
                    value={field.value}
                  >
                    <Radio value={PERPETUAL_OFFERING_TYPES.living}>żyjącą</Radio>
                    <Radio value={PERPETUAL_OFFERING_TYPES.deceased}>zmarłą</Radio>
                  </Radio.Group>
                )}
              />
            </Space>
          </div>

          <div>
            <Controller
              control={control}
              name={perpetualMassFormNames.offeringAmount}
              render={({ field }) => {
                return (
                  <OfferingAmount
                    allowFreeOfferings={false}
                    clearErrors={clearErrors}
                    loading={loading}
                    massOfferingAmount={perpetualMassOfferingAmount}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    setError={setError}
                    value={field.value}
                    showLabel
                  />
                );
              }}
            />
            {errors[perpetualMassFormNames.offeringAmount] && (
              <FormInputError errorText={errors[perpetualMassFormNames.offeringAmount].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              autoComplete="name"
              type="text"
              id={perpetualMassFormNames.fullName}
              {...register(perpetualMassFormNames.fullName)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={perpetualMassFormNames.fullName}>Imię i nazwisko zamawiającego</label>

            {errors[perpetualMassFormNames.fullName] && (
              <FormInputError errorText={errors[perpetualMassFormNames.fullName].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              id={perpetualMassFormNames.email}
              type="email"
              autoComplete="email"
              {...register(perpetualMassFormNames.email)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={perpetualMassFormNames.email}>E-mail zamawiającego</label>

            {errors[perpetualMassFormNames.email] && (
              <FormInputError errorText={errors[perpetualMassFormNames.email].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              type="tel"
              autoComplete="tel"
              id={perpetualMassFormNames.phone}
              {...register(perpetualMassFormNames.phone)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={perpetualMassFormNames.phone}>Numer telefonu zamawiającego</label>

            {errors[perpetualMassFormNames.phone] && (
              <FormInputError errorText={errors[perpetualMassFormNames.phone].message} />
            )}
          </div>

          {perpetualMassesFormAddressField && (
            <div>
              <div className="md-form input-group mt-0 mb-0">
                <input
                  disabled={loading}
                  className="form-check-input"
                  type="checkbox"
                  // autoComplete="tel"
                  id={perpetualMassFormNames.showAddress}
                  {...register(perpetualMassFormNames.showAddress)}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor={perpetualMassFormNames.showAddress}>
                  Chcę otrzymać papierowe potwierdzenie zamówienia intencji
                </label>
              </div>

              <AnimateHeight duration={500} height={showAddress ? 'auto' : 0}>
                <Address loading={loading} errors={errors} register={register} />
              </AnimateHeight>
            </div>
          )}

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-check-input"
              type="checkbox"
              value="1"
              id={perpetualMassFormNames.agreeOnRegulations}
              {...register(perpetualMassFormNames.agreeOnRegulations)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              className="form-check-label w-100"
              htmlFor={perpetualMassFormNames.agreeOnRegulations}
              id={`label-${perpetualMassFormNames.agreeOnRegulations}`}
            >
              Akceptuję{' '}
              <a target="_blank" rel="noreferrer" href={regulationsUrl}>
                Regulamin
              </a>
            </label>

            {errors[perpetualMassFormNames.agreeOnRegulations] && (
              <FormInputError
                className="mt-2"
                errorText={errors[perpetualMassFormNames.agreeOnRegulations].message}
              />
            )}
          </div>
        </Space>

        <SubmitAddMassOffering
          name={perpetualMassFormNames.offeringAmount}
          disabled={!isValid}
          watch={watch}
          loading={loading}
          isOnlinePayment
          isPaymentExpiring
        />
      </form>
    </div>
  );
};

PerpetualMassesForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};
